import React, { useEffect, useState } from 'react';
import { Sparkles, X, ArrowRight } from 'lucide-react';

const LS_KEY = 'neurowave_welcome_toast_seen_v1';

/**
 * Non-blocking welcome toast shown in the bottom-left corner.
 * Replaces the previous full-screen welcome modal so users can
 * interact with the app immediately.
 *
 * The onTakeTour callback opens the full OnboardingTour slideshow.
 */
const WelcomeToast = ({ onTakeTour, autoHideMs = 12000 }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let seen = false;
    try { seen = localStorage.getItem(LS_KEY) === 'true'; } catch { /* ignore */ }
    if (seen) return;
    // Small delay so the toast slides in after the app settles
    const t = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!visible || !autoHideMs) return;
    const t = setTimeout(() => dismiss(), autoHideMs);
    return () => clearTimeout(t);
     
  }, [visible, autoHideMs]);

  const dismiss = () => {
    try { localStorage.setItem(LS_KEY, 'true'); } catch { /* ignore */ }
    setVisible(false);
  };

  const takeTour = () => {
    dismiss();
    if (onTakeTour) onTakeTour();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed z-50 bottom-6 left-6 max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="welcome-toast"
    >
      <div className="bg-gradient-to-br from-[#1a1530]/95 to-[#0f0a1f]/95 backdrop-blur-xl border border-purple-500/20 rounded-2xl p-4 shadow-2xl">
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-white/30 hover:text-white/70"
          aria-label="Dismiss"
          data-testid="welcome-toast-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/30 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-purple-300" />
          </div>
          <div>
            <p className="text-white text-sm font-medium mb-1">
              Welcome to NeuroWave
            </p>
            <p className="text-white/55 text-xs leading-relaxed mb-3">
              Explore the app freely, or take a 30-second tour to learn the highlights.
            </p>
            <div className="flex gap-2">
              <button
                onClick={takeTour}
                className="text-xs bg-purple-500/15 hover:bg-purple-500/25 text-purple-200 border border-purple-500/30 rounded-lg px-3 py-1.5 transition-colors flex items-center gap-1"
                data-testid="welcome-toast-take-tour"
              >
                Quick tour <ArrowRight className="w-3 h-3" />
              </button>
              <button
                onClick={dismiss}
                className="text-xs text-white/40 hover:text-white/60 px-2 py-1.5"
                data-testid="welcome-toast-skip"
              >
                I'll explore
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeToast;
